import React, { useContext, useEffect, useState } from "react";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import SectionHeader from "../components/section/SectionHeader";
import {
  EDIT_REALTIME_PUBLISH_INFO_FORM_FIELDS,
  REALTIME_FILTER_COLUMNS,
  REALTIME_TABLE_HEADERS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  createSearchConfig,
  errorAlert,
  makeApiRequest,
  transformRealtimeRows,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import OrgContext from "../store/orgContext";
import InfoAlert from "../ui/InfoAlert";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import { getPageTranslationText } from "../translations/util";
import { Lang, Operation, Page } from "../translations/types";
import useAppTitle from "../hooks/useAppTitle";
import useHideSidePanel from "../hooks/useHideSidePanel";
import SearchContext from "../store/searchContext";

interface ISearchData {
  "GROUP-ID"?: string | null;
  "USER-ID"?: string | null;
}

const INITIAL_STATE = {
  "GROUP-ID": "",
  "USER-ID": "",
};

const searchFields = ["GROUP-ID", "USER-ID"];

const searchConfigNew = createSearchConfig(
  searchFields,
  EDIT_REALTIME_PUBLISH_INFO_FORM_FIELDS
);

const translations = getPageTranslationText({
  lang: Lang.EN,
  page: Page.PUBLISH_INFO,
  op: Operation.READ,
});

const PublishInfo = () => {
  const { token } = useContext(AuthContext);
  const { orgs: orgList, selectedOrg, updateOrg } = useContext(OrgContext);
  const { selectedColumn, setSelectedColumn, setSearchText, setDisableSearch } =
    useContext(SearchContext);
  const [loading, setLoading] = useState(false);
  useAppTitle("Publish Info- IWF Config Admin Tool (ICAT) by AT&T");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [messages, setMessages] = useState<any[]>([]);
  const { showAdvancedSearch, handleToggleAdvanceSearchPanel } =
    useHideSidePanel();
  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
  });

  const fetchRealtimeInfo = async () => {
    setLoading(true);
    try {
      if (selectedOrg) {
        const { data, pageNum, pageSize } = searchQuery;
        const queryParams = new URLSearchParams({
          page: `${pageNum}`,
          pageSize: `${pageSize}`,
          ...(data &&
            data["GROUP-ID"] && {
              "GROUP-ID": data["GROUP-ID"],
            }),
          ...(data &&
            data["USER-ID"] && {
              "USER-ID": data["USER-ID"],
            }),
        });
        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/realtime/mc/publish-info?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        setNumberOfRows(response.NbrOfRows);
        setNumberOfPages(response.NbrOfPages);
        setMessages(response.Rows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRealtimeInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, searchQuery]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  useEffect(() => {
    setSelectedColumn(null);
    setSearchText("");
    setDisableSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchData(INITIAL_STATE);
    setSearhQuery({
      pageSize: 20,
      pageNum: 1,
      data: {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  return (
    <>
      <SectionHeader
        pageTitle={translations.header}
        pageDescription={translations.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
          </div>
        }
      />

      {selectedOrg ? (
        <>
          {showAdvancedSearch ? (
            <AdvancedSearch
              setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
              advancedSearchState={searchData}
              advancedSearchConfig={searchConfigNew}
              setAdvancedSearchState={setSearchData}
              onSubmit={(data) =>
                setSearhQuery({ pageNum: 1, pageSize: 20, data })
              }
              onCancel={() => {
                setSearchData(INITIAL_STATE);
                handleToggleAdvanceSearchPanel();
              }}
            />
          ) : null}

          <DataTableWithControls
            loading={loading}
            filterColumns={REALTIME_FILTER_COLUMNS}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            onSearch={(term) =>
              setSearhQuery({
                pageNum: 1,
                pageSize: 20,
                data: { [`${selectedColumn}`]: term },
              })
            }
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            showCreateButton={false}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rows={transformRealtimeRows(messages)}
            numberOfAdvancedSearchFilters={searchConfigNew.reduce(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (acc, el: any) => {
                const { name } = el;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj: any = { ...searchData };
                return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
              },
              0
            )}
            clearAdvancedSearchFilters={() => {
              setSearchData(INITIAL_STATE);
              setSearhQuery({
                pageNum: 1,
                pageSize: 20,
                data: {},
              });
            }}
            headers={REALTIME_TABLE_HEADERS}
            pageNum={searchQuery.pageNum}
            setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
            totalItems={numberOfRows}
            totalPages={numberOfPages}
            pageSize={searchQuery.pageSize}
            setPageSize={(pageSize) =>
              setSearhQuery({ ...searchQuery, pageSize })
            }
          />
        </>
      ) : (
        <InfoAlert
          heading={translations.infoAlert.heading}
          subText={translations.infoAlert.subText}
        />
      )}
    </>
  );
};

export default PublishInfo;
